import { IsIn, IsISO8601, IsNotEmpty, IsOptional, IsString, MaxLength } from 'class-validator';

/**
 * What kind of externally authored artefact this is.
 *
 * `expert_report` is load-bearing: an attached expert report is one of the two
 * ways the Stage 4 s. 131(3) gate can be satisfied (the other being the legacy
 * free-text `expertReportRef`).
 */
export const FILE_DOCUMENT_KINDS = [
  'expert_report',
  'third_party',
  // Service New Brunswick land-registry records, uploaded at Stage 1 to identify
  // the subject property. Kept as their own kinds rather than folded into
  // `third_party` because upload-and-extract keys off them, and because the
  // Notice to Comply's property description traces back to these specifically.
  'snb_pan',
  'snb_parcel',
  'other',
] as const;
export type FileDocumentKind = (typeof FILE_DOCUMENT_KINDS)[number];

/** The SNB record kinds, which carry an extraction pipeline the others do not. */
export const SNB_DOCUMENT_KIND_SET: readonly FileDocumentKind[] = ['snb_pan', 'snb_parcel'];

/** A supporting document attached to a file, as returned by the API. */
export interface FileDocument {
  id: string;
  fileId: string;
  kind: FileDocumentKind;
  /** What kind it was, when `kind` is `other`. Null otherwise. */
  kindOther: string | null;
  title: string;
  /** Authoring firm or individual, when known. */
  authorName: string | null;
  /** Date on the document itself (ISO date), distinct from `createdAt`. */
  documentDate: string | null;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  note: string | null;
  createdAt: string;
  /** Short-lived presigned URL for fetching the binary. */
  downloadUrl: string;
}

/**
 * The text fields accompanying a multipart document upload. The binary rides as
 * the `document` form field. Everything arrives as a string — multipart has no
 * types — so the date is validated as ISO 8601 and truncated server-side.
 */
export class UploadFileDocumentDto {
  @IsIn(FILE_DOCUMENT_KINDS)
  kind!: FileDocumentKind;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  kindOther?: string;

  @IsString()
  @IsNotEmpty()
  @MaxLength(300)
  title!: string;

  @IsOptional()
  @IsString()
  @MaxLength(300)
  authorName?: string;

  @IsOptional()
  @IsISO8601()
  documentDate?: string;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}

/**
 * One enforcement file in the admin index.
 *
 * Admin has no file queue of its own, and it should not grow one — but a lookup
 * that only accepts a reference the administrator has to already know is not a
 * screen, it's a riddle. This is the minimum to recognise a file and pick it:
 * what it is called, where it is, and whether it has anything attached.
 */
export interface EnforcementFileRow {
  id: string;
  reference: string;
  address: string;
  stage: string;
  status: string;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
}
